// Auth service using cookie-based token storage.
// Tokens are set by the backend Lambda (/auth/callback, /auth/refresh, /auth/logout).
//
// Cookie layout (all SameSite=Lax; Secure):
//   vault_token         — ID token, non-HttpOnly (JS can read)
//   vault_access_token  — access token, non-HttpOnly (JS can read, used for API calls)
//   vault_refresh       — refresh token, HttpOnly (JS CANNOT read — server only)
//   vault_token_exp     — expiry as ms timestamp string, non-HttpOnly (JS can read)

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string; // not stored client-side; kept for interface compat
  expiresAt: number;
  issuedAt?: number;
}

// ---------------------------------------------------------------------------
// Private cookie helpers
// ---------------------------------------------------------------------------

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function clearCookies(): void {
  const clear = (name: string) => {
    document.cookie = `${name}=; Max-Age=0; Path=/; Secure; SameSite=Lax`;
  };
  clear('vault_token');
  clear('vault_access_token');
  clear('vault_token_exp');
  // vault_refresh is HttpOnly — only the server can clear it
}

function getTokensFromCookies(): AuthTokens | null {
  const accessToken = getCookie('vault_access_token');
  const idToken = getCookie('vault_token');
  const expStr = getCookie('vault_token_exp');
  if (!accessToken || !idToken || !expStr) return null;

  const expiresAt = parseInt(expStr, 10);
  const issuedAt = expiresAt - 86_400_000; // tokens are 24 h; approximate issuedAt

  return { accessToken, idToken, expiresAt, issuedAt };
}

// ---------------------------------------------------------------------------
// Exported auth object
// ---------------------------------------------------------------------------

export const auth = {
  // Redirect to Cognito hosted UI.
  login() {
    const url = new URL(`https://${COGNITO_DOMAIN}/login`);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', CLIENT_ID);
    url.searchParams.set('redirect_uri', window.location.origin + '/auth/callback');
    url.searchParams.set('scope', 'openid email profile');
    window.location.href = url.toString();
  },

  // OAuth callback is now handled server-side by the backend Lambda.
  // This stub exists only to avoid breaking any lingering call-sites.
  async handleCallback(_code: string): Promise<void> {
    // No-op: the Lambda handles the token exchange and sets cookies.
  },

  // Return valid tokens, refreshing via the server if the access token is
  // within the last 30% of its lifetime.
  async getTokens(): Promise<AuthTokens | null> {
    const accessToken = getCookie('vault_access_token');
    const idToken = getCookie('vault_token');
    const expStr = getCookie('vault_token_exp');
    if (!accessToken || !idToken || !expStr) return null;

    const expiresAt = parseInt(expStr, 10);
    const issuedAt = expiresAt - 86_400_000;
    const remaining = expiresAt - Date.now();
    const totalLife = 86_400_000;

    if (remaining / totalLife > 0.3) {
      return { accessToken, idToken, expiresAt, issuedAt };
    }

    return this.refresh();
  },

  // Ask the backend to issue new tokens using the HttpOnly refresh cookie.
  // On success the server sets fresh cookies; we re-read them.
  // Retries once after 1s to survive transient network errors.
  async refresh(): Promise<AuthTokens | null> {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch('/auth/refresh', {
          method: 'POST',
          credentials: 'same-origin',
        });
        if (res.ok) return getTokensFromCookies();
        // Non-OK response on last attempt: clear and logout
        if (attempt === 1) {
          clearCookies();
          return null;
        }
      } catch {
        // Network error on last attempt: return null without clearing cookies —
        // the user may be briefly offline; keep session alive for next
        // focus/visibilitychange attempt.
        if (attempt === 1) return null;
      }
      // Wait 1s before retry
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    return null;
  },

  // Navigate to the backend logout endpoint.
  // The Lambda clears the HttpOnly refresh cookie and redirects to Cognito logout.
  logout() {
    window.location.href = '/auth/logout';
  },

  isAuthenticated(): boolean {
    const expStr = getCookie('vault_token_exp');
    if (!expStr) return false;
    return Date.now() < parseInt(expStr, 10);
  },

  getUserEmail(): string | null {
    const idToken = getCookie('vault_token');
    if (!idToken) return null;
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1])) as Record<string, unknown>;
      return (payload.email as string) ?? null;
    } catch {
      return null;
    }
  },

  getUserId(): string | null {
    const idToken = getCookie('vault_token');
    if (!idToken) return null;
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1])) as Record<string, unknown>;
      return (payload.sub as string) ?? null;
    } catch {
      return null;
    }
  },

  getUserGroups(): string[] {
    const idToken = getCookie('vault_token');
    if (!idToken) return [];
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1])) as Record<string, unknown>;
      return (payload['cognito:groups'] as string[]) ?? [];
    } catch {
      return [];
    }
  },

  hasVaultAccess(): boolean {
    const groups = this.getUserGroups();
    return groups.includes('vault') || groups.includes('admin');
  },
};

// Re-export getCookie so useAuth.ts can use it without duplicating the logic.
export { getCookie };
